/** Roving focus for a 2-column inbox dropzone grid (one tab stop per group). */
import { useCallback, useEffect, useState } from 'react';

const GRID_COLUMNS = 2;

/**
 * @param {number} itemCount
 * @param {(index: number) => void} [onActivate]
 */
export function useInboxDropzoneGridRoving(itemCount, onActivate) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (itemCount <= 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((index) => Math.min(index, itemCount - 1));
  }, [itemCount]);

  const onKeyDown = useCallback(
    (event) => {
      if (itemCount <= 0) return;

      const { key } = event;
      if (
        key === 'ArrowRight' &&
        activeIndex < itemCount - 1
      ) {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, itemCount - 1));
        return;
      }
      if (key === 'ArrowLeft' && activeIndex > 0) {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (key === 'ArrowDown') {
        const next = activeIndex + GRID_COLUMNS;
        if (next < itemCount) {
          event.preventDefault();
          setActiveIndex(next);
        }
        return;
      }
      if (key === 'ArrowUp') {
        const prev = activeIndex - GRID_COLUMNS;
        if (prev >= 0) {
          event.preventDefault();
          setActiveIndex(prev);
        }
        return;
      }
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        onActivate?.(activeIndex);
      }
    },
    [activeIndex, itemCount, onActivate],
  );

  const getTabIndex = useCallback(
    (index) => (index === activeIndex ? 0 : -1),
    [activeIndex],
  );

  return { activeIndex, setActiveIndex, onKeyDown, getTabIndex };
}
